/**
 * Link a won deal to the account it became.
 *
 * Accounts are created in the clients app, never here — its API refuses a
 * POST — so "move to client" can only ever mean finding the account the
 * customer registered and tying the deal to it. That used to be a dropdown of
 * nine hundred names inside the deal editor, which nobody found and nobody
 * could use. This is a search box with the likely match already at the top:
 * the account whose email is the deal contact's email, then anything whose
 * name or email contains what was typed.
 */
import { useMemo, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { leadDetails, companyName } from '../../domain/crm.js';
import { parseDate, formatDate } from '../../lib/dates.js';
import Icon from '../../lib/icons.jsx';
import { Avatar, PlanPill, TypePill, EmptyState } from '../Primitives.jsx';
import Sheet from '../Sheet.jsx';

const norm = s => String(s || '').trim().toLowerCase();

export default function LinkClientSheet({ id, onClose }) {
  const { S, crm, rows, saveDeal, noteActivity, logAudit, toast } = useApp();
  const deal = crm.deals.find(d => d.id === id);
  const facts = deal ? leadDetails(crm, deal) : {};

  const [q, setQ] = useState('');

  /*
   * Ranked, not filtered: the exact-email match is the answer nine times in
   * ten, so it sits first whatever was typed. Below it, anything the query
   * touches; with nothing typed, the accounts whose name shares a word with
   * the deal, so the list is never empty on open.
   */
  const matches = useMemo(() => {
    if (!deal) return [];
    const email = norm(facts.email);
    const query = norm(q);
    const words = norm(deal.title).split(/\s+/).filter(w => w.length > 2);
    const company = norm(companyName(crm, deal.cid));

    const score = r => {
      const name = norm(r.name);
      const mail = norm(r.email);
      if (email && mail === email) return 100;
      if (query) {
        if (mail === query) return 90;
        if (name.includes(query) || mail.includes(query) || norm(r.phone).includes(query)) return 50;
        return 0;
      }
      if (company && name.includes(company)) return 40;
      if (words.some(w => name.includes(w))) return 30;
      return 0;
    };

    return rows
      .map(r => ({ r, s: score(r) }))
      .filter(x => x.s > 0)
      .sort((a, b) => b.s - a.s || String(a.r.name).localeCompare(String(b.r.name)))
      .slice(0, 12);
  }, [rows, deal, facts.email, q, crm]);

  const link = r => {
    saveDeal(id, { clientId: r.id });
    noteActivity(id, `Linked to client ${r.name}`);
    logAudit('link', `Deal → client: ${deal.title} → ${r.name}`);
    toast(`${deal.title} linked to ${r.name}`);
    onClose();
  };

  if (!deal) return null;

  return (
    <Sheet icon="users" tone="g-green" title="Link to a client account" onClose={onClose} saveLabel={null}>
      <div className="muted" style={{ marginBottom: 12 }}>
        <b>{deal.title}</b> is won. Accounts are created in the clients app when
        the customer registers — pick the one they registered, and this deal
        stops counting as “won but not yet a client”.
        {facts.email ? <> Looking for <b>{facts.email}</b>.</> : null}
      </div>

      <div className="srch" style={{ marginBottom: 12 }}>
        <Icon name="search" />
        <input
          autoFocus
          placeholder="Search accounts by name, email or phone…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
      </div>

      {matches.length ? (
        <div className="qlist">
          {matches.map(({ r, s }) => (
            <div className="q" key={r.id} onClick={() => link(r)}>
              <Avatar name={r.name} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{r.name}</b>
                <div className="why">
                  {r.email || 'no email'}
                  {r.regOn ? ` · joined ${formatDate(parseDate(r.regOn), false, S.dateFmt)}` : ''}
                </div>
              </div>
              {s === 100 ? <span className="pill p-green">same email</span> : null}
              <TypePill value={r.acctType} />
              <PlanPill value={r.plan} />
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon="users" title={q ? 'No account matches' : 'Type a name or email'}>
          {q
            ? 'They may not have registered yet. Once they do, the account appears here.'
            : 'The account is whatever the customer registered in the clients app.'}
        </EmptyState>
      )}
    </Sheet>
  );
}
